import { Col, Form, Row, Spin } from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Client from "redux/models/client";
import User from "redux/models/user";
import { selectUdf, setJobSourceUdf } from "redux/slices/udfSlice";
import K from "utilities/constants";
import { convertDateStringToMoment } from "utilities/dateUtility";
import {
  generateUdfSetFieldValues,
  generateUdfValuesObject,
  getFieldsSortedByOrder,
  removeUdfKeysFromObject,
} from "utilities/generalUtility";
import { lookupTablesForJobs } from "config/lookupTables";
import LookupTable from "redux/models/lookupTable";
import UDF from "redux/models/udf";
import { selectConfigration } from "redux/slices/configrationSlice";
import { ViewFieldOrder } from "redux/models/viewFieldOrder";
import { getDefaultJobFields } from "config/jobFields";
import { JobFieldRenderer } from "common/components/jobFieldRenderer/jobFieldRenderer";

export default function JobForm({
  form,
  clientId,
  editData,
  isDuplicate,
  ...props
}) {
  const dispatch = useDispatch();
  const lookupTables = useSelector(selectConfigration).lookup;

  const [isDataLoading, setIsDataLoading] = useState(false);
  const [jobTiers, setJobTiers] = useState([]);
  const udfSlice = useSelector(selectUdf);
  const [users, setUsers] = useState({ "Hiring Manager": [], Recruiter: [] });

  const [fieldsOrder, setFieldsOrder] = useState([]);

  const defaultFields = getDefaultJobFields(udfSlice[K.Redux.JobSourceUdf]);
  const fields = getFieldsSortedByOrder(defaultFields, fieldsOrder);

  const getJobUdfs = async (entityKeyId = null) => {
    const jobUdfs = await UDF.getEditUdfFields(
      {
        clientId,
        entityId: K.UDF.Entities.JobSource.id,
        entityKey: K.UDF.Entities.JobSource.key,
        entityKeyId,
        isVisible: 1,
      },
      false,
    );

    dispatch(setJobSourceUdf(jobUdfs));
  };

  const onFinish = async (values) => {
    const udfValues = generateUdfValuesObject(
      udfSlice[K.Redux.JobSourceUdf],
      values,
    );
    const updatedValues = removeUdfKeysFromObject(
      values,
      udfSlice[K.Redux.JobSourceUdf],
    );

    values = { ...updatedValues, udfValues };

    props.onFinish?.(values);
  };

  const fetchFieldsOrder = async () => {
    try {
      const { data } = await ViewFieldOrder.getForClientByType(
        clientId,
        K.ViewTypes.JobForm,
        false,
      );

      setFieldsOrder(data?.fieldOrder ?? []);
    } catch (error) {
      console.error("Error fetching saved field order:", error);
    }
  };

  const getClientTiers = async () => {
    try {
      const res = await Client.getTiersById(clientId, false);
      setJobTiers(res);
    } catch (err) {
      console.error(err);
    }
  };

  const getClientHiringManager = async () => {
    try {
      const body = { clientIds: [clientId] };
      const res = await User.getClientHiringManager(body, false);
      setUsers((prev) => ({ ...prev, "Hiring Manager": res }));
    } catch (err) {
      console.error(err);
    }
  };

  const getClientRecruiter = async () => {
    try {
      const { data } = await Client.v2.getRecruitersByClientId(
        clientId,
        { view: "extended" },
        false,
      );

      setUsers((prev) => ({ ...prev, Recruiter: data }));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchData = async () => {
    try {
      setIsDataLoading(true);

      await Promise.all([
        fetchFieldsOrder(),
        getClientHiringManager(),
        getClientTiers(),
        getClientRecruiter(),
        getJobUdfs(editData?.id),
        ...lookupTablesForJobs.map(async (item) => {
          const apiEndPoint = item.isClient
            ? `${item.apiEndPoint}/${clientId}`
            : item.apiEndPoint;

          await dispatch(
            LookupTable.getData(item.reduxKey, apiEndPoint, [], false, false),
          );
        }),
      ]);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!editData) return;
    form.setFieldsValue({
      title: editData.title,
      jobTiersId: editData.jobTier?.id,
      jobTiersTitlesId: editData.jobTierTitle?.id,
      managerId: editData.jobHiringManagers?.map(
        ({ hiringManager }) => hiringManager.id,
      ),
      jobRecruiters: editData.jobRecruiters?.map(
        ({ recruiter }) => recruiter.id,
      ),
      jobLocationsId: editData.jobLocation?.id,
      jobStatusesId: editData.jobStatus?.id,
      jobDepartmentsId: editData.jobDepartment?.id,
      jobGroupsId: editData.jobGroup?.id,
      maxPay: editData.maxPay,
      minPay: editData.minPay,
      recruitingStartDate: isDuplicate
        ? null
        : convertDateStringToMoment(editData.recruitingStartDate),
      jobOriginalOpenings: isDuplicate
        ? null
        : editData.originalVacancy?.currentOpenings ?? 0,
      extAtsId: isDuplicate ? null : editData.extAtsId,
      extSecondaryAtsId: isDuplicate ? null : editData.extSecondaryAtsId,
      notes: isDuplicate ? null : editData.notes,
      ...generateUdfSetFieldValues(udfSlice[K.Redux.JobSourceUdf]),
    });
  }, [editData, form, isDuplicate, udfSlice]);

  return (
    <Spin spinning={isDataLoading}>
      <Form
        disabled={isDataLoading}
        layout="vertical"
        form={form}
        onFinish={onFinish}
      >
        <Row gutter={28}>
          {fields.map((field) => {
            const context = {
              form,
              jobTiers,
              users,
              lookupTables,
              clientId,
              editData,
              isDuplicate,
              disabled: isDataLoading,
            };

            return (
              <Col xs={24} sm={field.span || 12} key={field.key}>
                <JobFieldRenderer field={field} context={context} />
              </Col>
            );
          })}
        </Row>
      </Form>
    </Spin>
  );
}
